import { useGetKosherFilter } from '@framework/filters/get-kosher-filter';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@i18n/client';
import { useFetchManufacturersFilter } from '@framework/filters/get-manufacturers-filter';
import { IoChevronDown, IoClose } from 'react-icons/io5';
import { useLocation } from 'react-router-dom';
import useQueryParam from '@utils/use-query-params';
import { getFilterNameForLanguage } from '@framework/types';
import { useUI } from '@contexts/ui.context';

export const TopFilters: React.FC<{ lang: string }> = ({ lang }) => {
  const { t } = useTranslation(lang);
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const { updateQueryParams, clearQueryParam } = useQueryParam(pathname ?? '/');
  const { closeFilter } = useUI();

  const {
    data: kosherItems,
    isLoading: isKosherFilterLoading,
    error: kosherItemsError,
  } = useGetKosherFilter();

  const {
    data: manufacturersItems,
    isLoading: isManufacturersFilterLoading,
    error: manufacturersFilterError,
  } = useFetchManufacturersFilter();

  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenFilter(null);
        setDropdownPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFilterSelect = (filterKey: string, value: string) => {
    if (searchParams.get(filterKey) === value) {
      clearQueryParam([filterKey]);
    } else {
      updateQueryParams(filterKey, value);
    }
    setOpenFilter(null);
    setDropdownPosition(null);
    closeFilter();
  };

  const getSelectedValue = (filterKey: string) => {
    const value = searchParams.get(filterKey);
    if (!value) return null;

    switch (filterKey) {
      case 'manufacturer':
        return manufacturersItems?.find((item) => item.slug === value);
      case 'kosher':
        return kosherItems?.find((item) => item.slug === value);
      default:
        return null;
    }
  };

  const getQuickFilterLabel = () => {
    if (searchParams.get('hide_out_of_stock'))
      return t('common:text-hide-out-of-stock');
    if (searchParams.get('kosher_pesach'))
      return t('common:text-show-only-kosher_pesach-product');
    if (searchParams.get('on_sale'))
      return t('common:text-show-only-on_sale-product');
    return null;
  };

  const FilterDropdown = ({
    filterKey,
    label,
    items,
    isLoading,
    error,
  }: {
    filterKey: string;
    label: string;
    items: any[];
    isLoading: boolean;
    error: any;
  }) => {
    const isOpen = openFilter === filterKey;
    const selectedItem = getSelectedValue(filterKey);
    const buttonRef = useRef<HTMLButtonElement>(null);

    if (isLoading) return null;
    if (error) return null;

    const handleButtonClick = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
      setOpenFilter(isOpen ? null : filterKey);
    };

    return (
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          className="flex items-center px-3 py-2 text-sm border border-border-base rounded-md hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand focus:ring-opacity-50 transition-colors"
        >
          <span className="text-brand-dark font-medium">{label}</span>
          <span className="text-gray-500 ltr:ml-2 rtl:mr-2">
            {selectedItem
              ? getFilterNameForLanguage(selectedItem, lang)
              : t('text-all')}
          </span>
          {selectedItem && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterSelect(filterKey, '');
              }}
              className="ltr:ml-2 rtl:mr-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
              title={t('text-clear')}
            >
              <IoClose className="w-3 h-3 text-gray-500 hover:text-red-600" />
            </button>
          )}
          <IoChevronDown
            className={`w-4 h-4 text-gray-400 ltr:ml-2 rtl:mr-2 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && dropdownPosition && createPortal(
          <div 
            className="fixed bg-white border border-border-base rounded-md shadow-lg z-[9999]"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: Math.max(dropdownPosition.width, 192), // min width of 48 * 4 = 192px
              maxHeight: '240px',
              overflowY: 'auto'
            }}
          >
            <div className="py-1">
              <button
                onClick={() => handleFilterSelect(filterKey, '')}
                className={`w-full text-left rtl:text-right px-4 py-2 text-sm hover:bg-gray-50 ${!selectedItem ? 'bg-gray-50 text-brand' : 'text-gray-700'}`}
              >
                {t('text-all')}
              </button>
              {items?.map((item) => (
                <button
                  key={item.slug}
                  onClick={() => handleFilterSelect(filterKey, item.slug)}
                  className={`w-full text-left rtl:text-right px-4 py-2 text-sm hover:bg-gray-50 ${selectedItem?.slug === item.slug ? 'bg-gray-50 text-brand' : 'text-gray-700'}`}
                >
                  {getFilterNameForLanguage(item, lang)}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  const QuickFilterDropdown = () => {
    const isOpen = openFilter === 'quick';
    const selectedLabel = getQuickFilterLabel();
    const buttonRef = useRef<HTMLButtonElement>(null);

    const quickFilterItems = [
      {
        key: 'hide_out_of_stock',
        label: t('common:text-hide-out-of-stock'),
        value: 'true',
      },
      {
        key: 'kosher_pesach',
        label: t('common:text-show-only-kosher_pesach-product'),
        value: 'show-only-pesach-kosher',
      },
      {
        key: 'on_sale',
        label: t('common:text-show-only-on_sale-product'),
        value: 'show-only-on_sale',
      },
    ];

    const handleButtonClick = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        });
      }
      setOpenFilter(isOpen ? null : 'quick');
    };

    return (
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          className="flex items-center px-3 py-2 text-sm border border-border-base rounded-md hover:border-brand focus:outline-none focus:ring-2 focus:ring-brand focus:ring-opacity-50 transition-colors"
        >
          <span className="text-brand-dark font-medium">
            {t('text-quick-filters')}
          </span>
          <span className="text-gray-500 ltr:ml-2 rtl:mr-2">
            {selectedLabel || t('text-all')}
          </span>
          {selectedLabel && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                clearQueryParam([
                  'hide_out_of_stock',
                  'kosher_pesach',
                  'on_sale',
                ]);
                closeFilter();
              }}
              className="ltr:ml-2 rtl:mr-2 p-1 hover:bg-gray-100 rounded-full transition-colors"
              title={t('text-clear')}
            >
              <IoClose className="w-3 h-3 text-gray-500 hover:text-red-600" />
            </button>
          )}
          <IoChevronDown
            className={`w-4 h-4 text-gray-400 ltr:ml-2 rtl:mr-2 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && dropdownPosition && createPortal(
          <div 
            className="fixed bg-white border border-border-base rounded-md shadow-lg z-[9999]"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: Math.max(dropdownPosition.width, 192), // min width of 48 * 4 = 192px
              maxHeight: '240px',
              overflowY: 'auto'
            }}
          >
            <div className="py-1">
              <button
                onClick={() => {
                  clearQueryParam([
                    'hide_out_of_stock',
                    'kosher_pesach',
                    'on_sale',
                  ]);
                  setOpenFilter(null);
                  closeFilter();
                }}
                className={`w-full text-left rtl:text-right px-4 py-2 text-sm hover:bg-gray-50 ${!selectedLabel ? 'bg-gray-50 text-brand' : 'text-gray-700'}`}
              >
                {t('text-all')}
              </button>
              {quickFilterItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleFilterSelect(item.key, item.value)}
                  className={`w-full text-left rtl:text-right px-4 py-2 text-sm hover:bg-gray-50 ${searchParams.get(item.key) ? 'bg-gray-50 text-brand' : 'text-gray-700'}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className="flex items-center space-x-3 rtl:space-x-reverse"
    >
      <QuickFilterDropdown />

      <FilterDropdown
        filterKey="manufacturer"
        label={t('text-manufacturer-needs')}
        items={manufacturersItems ?? []}
        isLoading={isManufacturersFilterLoading}
        error={manufacturersFilterError}
      />

      <FilterDropdown
        filterKey="kosher"
        label={t('text-kosher-needs')}
        items={kosherItems ?? []}
        isLoading={isKosherFilterLoading}
        error={kosherItemsError}
      />
    </div>
  );
};
